import React, { useEffect, useState } from 'react';
import { Monitor, Droplets, Users, Shield, Gauge, Database, ClipboardCheck, ChevronLeft, ChevronRight, X } from 'lucide-react';

const Projects = () => {
  const [activeGallery, setActiveGallery] = useState(null);

  const closeGallery = () => setActiveGallery(null);

  const showPreviousImage = () => {
    setActiveGallery((gallery) => {
      if (!gallery) return gallery;
      return {
        ...gallery,
        imageIndex: (gallery.imageIndex - 1 + gallery.images.length) % gallery.images.length
      };
    });
  };

  const showNextImage = () => {
    setActiveGallery((gallery) => {
      if (!gallery) return gallery;
      return {
        ...gallery,
        imageIndex: (gallery.imageIndex + 1) % gallery.images.length
      };
    });
  };

  useEffect(() => {
    if (!activeGallery) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeGallery();
      if (event.key === 'ArrowLeft') showPreviousImage();
      if (event.key === 'ArrowRight') showNextImage();
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeGallery]);

  const projects = [
    {
      icon: <Monitor className="w-10 h-10" />,
      title: 'Energy Monitoring System (EMS)',
      description: 'Comprehensive real-time energy monitoring with SCADA integration for industrial facilities.',
      features: ['Real-time meter data monitoring', 'Consumption tracking and analytics', 'Trend graphs and reporting', 'Alert and notification system'],
      images: [
        'https://customer-assets.emergentagent.com/job_usha-control/artifacts/eq1vqrqf_WhatsApp%20Image%202026-02-19%20at%2010.29.28%20PM.jpeg',
        'https://customer-assets.emergentagent.com/job_usha-control/artifacts/ucbif5er_WhatsApp%20Image%202026-02-19%20at%2010.29.43%20PM.jpeg',
        'https://customer-assets.emergentagent.com/job_usha-control/artifacts/e4ron5m5_WhatsApp%20Image%202026-02-19%20at%2010.29.49%20PM.jpeg',
        'https://customer-assets.emergentagent.com/job_usha-control/artifacts/mxunnluh_WhatsApp%20Image%202026-02-19%20at%2010.29.54%20PM.jpeg'
      ]
    },
    {
      icon: <Droplets className="w-10 h-10" />,
      title: 'Water Monitoring System (WMS)',
      description: 'Advanced water flow monitoring and management system with cloud-based SCADA dashboard.',
      features: ['Flow monitoring with totalizer', 'Cloud-based dashboard', 'Daily and hourly reporting', 'Trend visualization']
    },
    {
      icon: <Shield className="w-10 h-10" />,
      title: 'LPG Automatic Remote Valve Control Application',
      description: 'We have successfully developed and implemented Automatic Remote Valve Control Applications for LPG facilities. The solution is designed to satisfy OISD-214 requirements and enhances operational safety through centralized valve monitoring and control.',
      features: ['Centralized valve monitoring & control', 'Successfully installed at multiple industrial locations', 'Real field implementation experience', 'Industrial safety-focused design'],
      images: [
        'https://customer-assets.emergentagent.com/job_usha-control/artifacts/l1kgkbit_WhatsApp%20Image%202026-02-19%20at%2010.32.56%20PM.jpeg',
        'https://customer-assets.emergentagent.com/job_usha-control/artifacts/rwth4bi6_WhatsApp%20Image%202026-02-19%20at%2010.33.02%20PM.jpeg',
        'https://customer-assets.emergentagent.com/job_usha-control/artifacts/tkk63w4c_WhatsApp%20Image%202026-02-19%20at%2010.33.08%20PM.jpeg',
        'https://customer-assets.emergentagent.com/job_usha-control/artifacts/k30qp10c_WhatsApp%20Image%202026-02-19%20at%2010.33.17%20PM.jpeg',
        'https://customer-assets.emergentagent.com/job_usha-control/artifacts/6innw7v7_WhatsApp%20Image%202026-02-19%20at%2010.33.40%20PM.jpeg',
        'https://customer-assets.emergentagent.com/job_usha-control/artifacts/fb722p8f_WhatsApp%20Image%202026-02-19%20at%2010.32.43%20PM.jpeg',
        'https://customer-assets.emergentagent.com/job_usha-control/artifacts/jh6nctun_WhatsApp%20Image%202026-02-19%20at%2010.32.50%20PM.jpeg',
        'https://customer-assets.emergentagent.com/job_usha-control/artifacts/bbianqv1_WhatsApp%20Image%202026-02-19%20at%2010.32.56%20PM.jpeg'
      ]
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: 'Access Control & Headcount Monitoring System',
      description: 'AI-based real-time headcount monitoring and access control system for industrial safety.',
      features: ['Real-time headcount tracking', 'AI-face recognition', 'Access control integration', 'Emergency response support']
    },
    {
      icon: <ClipboardCheck className="w-10 h-10" />,
      title: 'Live Work Permit Management System',
      description: 'Digital work permit management system developed following OISD-105 guidelines for safe industrial operations.',
      features: ['Live permit status tracking', 'Digital approval workflow', 'Developed following OISD-105 guidelines', 'Safety zone management']
    },
    {
      icon: <Database className="w-10 h-10" />,
      title: 'Tank Farm Management System (TFMS)',
      description: 'Integrated monitoring and control solution for tank farm operations including level monitoring, status indication, reporting, alarms, and centralized SCADA-based visualization.',
      features: ['Real-time monitoring', 'Alarm management', 'Historical data logging', 'Operational efficiency']
    }
  ];

  return (
    <div>
      <section 
        className="relative py-24 bg-[#FF7F2A] text-white overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1581092583537-20d51876f8f5?q=80&w=2070&auto=format&fit=crop)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-[#FF7F2A]/95"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 lg:px-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4" data-testid="projects-heading">
            Our Projects
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            Demonstrating safety and OISD requirements through proven, real-world industrial implementations
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="space-y-12">
            {projects.map((project, index) => (
              <div 
                key={index}
                className={`border-2 border-slate-200 rounded-sm overflow-hidden hover:border-[#1E56A0] hover:shadow-lg transition-all duration-300 ${
                  index % 2 === 0 ? 'bg-white' : 'bg-slate-50'
                }`}
                data-testid={`project-card-${index}`}
              >
                <div className="p-8 md:p-10">
                  <div className="flex items-start gap-6 mb-6">
                    <div className="bg-[#1E56A0] text-white rounded-sm p-4 flex-shrink-0">
                      {project.icon}
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-3">{project.title}</h3>
                      <p className="text-base leading-relaxed text-slate-600 mb-6">
                        {project.description}
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {project.features.map((feature, fIndex) => (
                          <div key={fIndex} className="flex items-start space-x-3 bg-white p-3 rounded-sm border border-slate-200">
                            <div className="w-2 h-2 bg-[#FF7F2A] rounded-full mt-1.5 flex-shrink-0"></div>
                            <span className="text-sm text-slate-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                      {project.images && (
                        <div className="mt-8">
                          <h4 className="text-lg font-semibold text-slate-900 mb-4">Project Gallery</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {project.images.map((image, imgIndex) => (
                              <button
                                key={imgIndex}
                                type="button"
                                onClick={() => setActiveGallery({
                                  images: project.images,
                                  title: project.title,
                                  imageIndex: imgIndex
                                })}
                                className="relative group overflow-hidden rounded-sm border-2 border-slate-200 hover:border-[#1E56A0] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF7F2A] focus-visible:ring-offset-2"
                                aria-label={`Open ${project.title} screenshot ${imgIndex + 1} in photo viewer`}
                              >
                                <img 
                                  src={image} 
                                  alt={`${project.title} Screenshot ${imgIndex + 1}`}
                                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {project.scadaNote && (
                        <div className="mt-6 p-4 bg-slate-100 rounded-sm border-l-4 border-[#1E56A0]">
                          <p className="text-sm text-slate-600 italic">{project.scadaNote}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="border-2 border-slate-200 rounded-sm overflow-hidden bg-white hover:border-[#1E56A0] hover:shadow-lg transition-all duration-300">
            <div className="p-8 md:p-10">
              <div className="flex items-start gap-6 mb-6">
                <div className="bg-[#FF7F2A] text-white rounded-sm p-4 flex-shrink-0">
                  <Gauge className="w-10 h-10" />
                </div>
                <div className="flex-grow">
                  <h3 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-3">
                    Fire Engine Auto Sequence with Backup Engine
                  </h3>
                  <p className="text-base leading-relaxed text-slate-600 mb-6">
                    Automated fire pump sequencing system with priority-based engine selection and backup engine activation for critical safety applications.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                    <div className="flex items-start space-x-3 bg-slate-50 p-3 rounded-sm border border-slate-200">
                      <div className="w-2 h-2 bg-[#FF7F2A] rounded-full mt-1.5 flex-shrink-0"></div>
                      <span className="text-sm text-slate-700">Priority-based sequencing</span>
                    </div>
                    <div className="flex items-start space-x-3 bg-slate-50 p-3 rounded-sm border border-slate-200">
                      <div className="w-2 h-2 bg-[#FF7F2A] rounded-full mt-1.5 flex-shrink-0"></div>
                      <span className="text-sm text-slate-700">Automatic backup activation</span>
                    </div>
                    <div className="flex items-start space-x-3 bg-slate-50 p-3 rounded-sm border border-slate-200">
                      <div className="w-2 h-2 bg-[#FF7F2A] rounded-full mt-1.5 flex-shrink-0"></div>
                      <span className="text-sm text-slate-700">Pressure monitoring and control</span>
                    </div>
                    <div className="flex items-start space-x-3 bg-slate-50 p-3 rounded-sm border border-slate-200">
                      <div className="w-2 h-2 bg-[#FF7F2A] rounded-full mt-1.5 flex-shrink-0"></div>
                      <span className="text-sm text-slate-700">Real-time status indication</span>
                    </div>
                  </div>
                  
                  <div className="bg-slate-900 rounded-sm overflow-hidden">
                    <video 
                      controls 
                      muted
                      className="w-full h-auto"
                      poster=""
                      data-testid="fire-engine-video"
                    >
                      <source src="https://customer-assets.emergentagent.com/job_usha-control/artifacts/bm3lt8tr_Video%20for%20fire%20engine%20project.mp4" type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                    <div className="p-4 bg-slate-800 text-white text-sm">
                      <p className="font-medium">Fire Engine Auto Sequence System - Project Video</p>
                      <p className="text-slate-400 text-xs mt-1">Click play to view the system in operation (muted by default)</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {activeGallery && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/90 p-4 md:p-8"
          role="dialog"
          aria-modal="true"
          aria-label={`${activeGallery.title} photo viewer`}
          onClick={closeGallery}
        >
          <button
            type="button"
            onClick={closeGallery}
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label="Close photo viewer"
          >
            <X size={28} />
          </button>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              showPreviousImage();
            }}
            className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white md:left-8"
            aria-label="Show previous image"
          >
            <ChevronLeft size={36} />
          </button>

          <div
            className="flex max-h-full max-w-6xl flex-col items-center gap-4"
            onClick={(event) => event.stopPropagation()}
          >
            <img
              src={activeGallery.images[activeGallery.imageIndex]}
              alt={`${activeGallery.title} Screenshot ${activeGallery.imageIndex + 1}`}
              className="max-h-[78vh] max-w-full rounded-sm object-contain shadow-2xl"
            />
            <p className="text-center text-sm font-medium text-white/90">
              {activeGallery.title} · {activeGallery.imageIndex + 1} of {activeGallery.images.length}
            </p>
          </div>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              showNextImage();
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white md:right-8"
            aria-label="Show next image"
          >
            <ChevronRight size={36} />
          </button>
        </div>
      )}

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Start Your Project With Us
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Ready to transform your industrial operations? Let's discuss your project requirements.
          </p>
          <a 
            href="/contact"
            className="inline-flex items-center bg-[#1E56A0] text-white hover:bg-[#164e8a] rounded-sm px-8 py-4 font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
          >
            Get Started
          </a>
        </div>
      </section>
    </div>
  );
};

export default Projects;
